import { useState } from 'react'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { FilterBar } from '@/shared/ui/FilterBar'
import { StatusBadge } from '@/shared/ui/Badge'
import { Check, X, Loader2 } from 'lucide-react'
import { formatDate } from '@/shared/utils'
import { useDebounce } from '@/shared/hooks'
import { useUnattendedStudents, useApproveRejectUnattended } from '@/api/hooks'

interface Session {
  id:number; booking_id?:number; student_name?:string; trainer_name?:string
  session_date?:string; status?:string; reason?:string
}

export function UnattendedStudentsPage() {
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)
  const { data, isLoading, isError } = useUnattendedStudents({ search: debounced || undefined })
  const action = useApproveRejectUnattended()

  const rows: Session[] = data?.data ?? data ?? []

  const decide = (s: Session, decision: 'APPROVED'|'REJECTED') =>
    action.mutate({ id: s.id, booking_id: s.booking_id, status: decision })

  return (
    <div className="space-y-5">
      <PageHeader title="Unattended Students"
        subtitle="Sessions flagged where the student did not show up"
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Unattended Students'}]}/>
      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar search={{ value:search, onChange:setSearch, placeholder:'Search student or trainer…' }}/>
        </div>
        {isError && <p className="px-5 py-4 text-sm text-red-500">Failed to load sessions.</p>}
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Booking</th><th>Student</th><th>Trainer</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-12 text-sm">No unattended sessions</td></tr>
              ) : rows.map(s => (
                <tr key={s.id}>
                  <td className="font-mono text-xs">#{s.booking_id ?? s.id}</td>
                  <td className="font-medium text-gray-900">{s.student_name ?? '—'}</td>
                  <td className="text-gray-700">{s.trainer_name ?? '—'}</td>
                  <td className="text-gray-500 text-sm">{s.session_date ? formatDate(s.session_date) : '—'}</td>
                  <td>{s.status ? <StatusBadge status={s.status}/> : '—'}</td>
                  <td>
                    {(!s.status || s.status.toLowerCase()==='pending') ? (
                      <div className="flex gap-1">
                        <button onClick={()=>decide(s,'APPROVED')} disabled={action.isPending}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50"><Check size={13}/></button>
                        <button onClick={()=>decide(s,'REJECTED')} disabled={action.isPending}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50"><X size={13}/></button>
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  )
}
